import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  analyzeMobileNumber,
  analyzePin,
  getMyNumerologyReport,
  getMyNumerologyReports,
  getNumerologyOptions,
} from "../../api/mobileNumerologyApi";
import { checkDob, DOB_ERROR, formatDob, maskDob } from "../../utils/dob";
import "./MobileNumerology.css";

const VERDICTS = {
  RECOMMENDED: { label: "Recommended", cls: "good", note: "This number looks favourable for you." },
  ACCEPTABLE: { label: "Acceptable", cls: "mixed", note: "Usable, but a few points need care." },
  NOT_RECOMMENDED: { label: "Not recommended", cls: "bad", note: "This number conflicts with traditional rules." },
};

// Mobile results are coloured by SCORE: 90-100 dark green, 70-89 light green, below 70 red.
const bandOf = (score) =>
  score >= 90
    ? { label: "Excellent", cls: "excellent", note: "This number looks very favourable for you." }
    : score >= 70
      ? { label: "Good", cls: "good", note: "This number looks favourable for you." }
      : { label: "Not recommended", cls: "bad", note: "This number conflicts with traditional rules." };

const SEVERITY = {
  critical: { title: "Must avoid", cls: "bad" },
  warning: { title: "Needs care", cls: "mixed" },
  info: { title: "Worth knowing", cls: "info" },
  positive: { title: "Favourable", cls: "good" },
};

const RATING_COLOUR = { excellent: "green", good: "green", mixed: "yellow", caution: "red", avoid: "red" };
const pairColour = (p) => p.colour || RATING_COLOUR[p.rating] || "";
// Hostile pairs and 4th aspects were removed from the reading; hide them in old saved reports.
const KEPT_PATTERN = (p) => p.kind !== "Hostile pair" && p.kind !== "4th aspect";

function Verdict({ verdict, score }) {
  const v = typeof score === "number" ? bandOf(score) : VERDICTS[verdict] || VERDICTS.ACCEPTABLE;
  return (
    <div className={`mn-verdict is-${v.cls}`}>
      <div>
        <span className="mn-verdict-label">{v.label}</span>
        <p>{v.note}</p>
      </div>
      {typeof score === "number" && (
        <div className="mn-score" aria-label={`Score ${score} out of 100`}>
          <strong>{score}</strong>
          <small>/ 100</small>
        </div>
      )}
    </div>
  );
}

function Findings({ findings }) {
  return (
    <div className="mn-findings">
      {["critical", "warning", "info", "positive"].map((sev) => {
        const items = findings.filter((f) => f.severity === sev);
        if (!items.length) return null;
        return (
          <section key={sev} className={`mn-finding-group is-${SEVERITY[sev].cls}`}>
            <h4>
              {SEVERITY[sev].title} <span>{items.length}</span>
            </h4>
            <ul>
              {items.map((f, i) => (
                <li key={`${f.code}-${i}`}>
                  <strong>{f.title}</strong>
                  <p>{f.detail}</p>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function Grid({ grid }) {
  return (
    <div className="mn-grid-wrap">
      <div className="mn-grid" role="table" aria-label="Vedic grid">
        {grid.layout.flat().map((cell) => (
          <div key={cell.digit} className={`mn-cell ${cell.count ? "is-on" : ""}`} role="cell">
            <span className="mn-cell-digit">{cell.digit}</span>
            {cell.count > 0 && <span className="mn-cell-count">{String(cell.digit).repeat(cell.count)}</span>}
          </div>
        ))}
      </div>
      <p className="mn-grid-missing">
        Missing numbers: <strong>{grid.missing.length ? grid.missing.join(", ") : "none"}</strong>
      </p>
    </div>
  );
}

function MobileResult({ result }) {
  const { profile, total, pairs, grid, professionFit } = result;
  return (
    <div className="mn-result">
      <Verdict verdict={result.verdict} score={result.score} />

      <div className="mn-facts">
        <div>
          <small>Mobile total</small>
          <strong>{total.total}</strong>
          <span>from {total.compound}</span>
        </div>
        {profile ? (
          <>
            <div>
              <small>Birth number</small>
              <strong>{profile.birthNumber}</strong>
            </div>
            <div>
              <small>Destiny number</small>
              <strong>{profile.destinyNumber}</strong>
            </div>
            <div>
              <small>Lucky totals for you</small>
              <strong>{total.luckyTotals.join(", ") || "-"}</strong>
            </div>
            {profile.chargingDirection && (
              <div>
                <small>Phone charging direction</small>
                <strong>{profile.chargingDirection}</strong>
              </div>
            )}
          </>
        ) : (
          <div>
            <small>Date of birth</small>
            <strong>Not given</strong>
            <span>add it for a personal result</span>
          </div>
        )}
      </div>

      <h3 className="mn-h">What we found</h3>
      {/* Grid patterns are listed with the grid below, not repeated here. */}
      <Findings findings={result.findings.filter((f) => !f.code.startsWith("GRID_"))} />

      <h3 className="mn-h">Pairs in your number</h3>
      <p className="mn-hint">
        Read left to right; zeros are skipped when pairing. The last 6 digits carry more weight than the first 4 (underlined).
      </p>
      <div className="mn-legend">
        <span className="mn-pair is-green">Best</span>
        <span className="mn-pair is-yellow">Neutral</span>
        <span className="mn-pair is-red">Avoid</span>
      </div>
      <div className="mn-pairs">
        {pairs.map((p) => (
          <span
            key={`${p.index}-${p.pair}`}
            className={`mn-pair ${pairColour(p) ? `is-${pairColour(p)}` : ""} ${p.zone === "last6" ? "is-last6" : ""}`}
            title={p.title || (p.double ? "Same-digit pair" : "")}
          >
            {p.pair}
          </span>
        ))}
      </div>

      <h3 className="mn-h">Vedic grid</h3>
      <Grid grid={grid} />
      {grid.patterns.filter(KEPT_PATTERN).length > 0 && (
        <ul className="mn-patterns">
          {grid.patterns.filter(KEPT_PATTERN).map((p) => (
            <li key={`${p.kind}-${p.cells.join("-")}`} className={`is-${RATING_COLOUR[p.rating] || "yellow"}`}>
              <strong>
                {p.kind} {p.cells.join("-")}
              </strong>{" "}
              - {p.title}
              <p>{p.summary}</p>
            </li>
          ))}
        </ul>
      )}

      {professionFit && (
        <>
          <h3 className="mn-h">Fit for: {professionFit.title}</h3>
          <p className="mn-hint">
            {professionFit.fits
              ? `Your number contains the lucky internal numbers: ${professionFit.matched.join(", ")}.`
              : "None of the lucky internal numbers for this profession are present."}
          </p>
        </>
      )}

      <p className="mn-disclaimer">{result.disclaimer}</p>
    </div>
  );
}

function PinResult({ result }) {
  return (
    <div className="mn-result">
      <Verdict verdict={result.verdict} />
      <div className="mn-facts">
        <div>
          <small>PIN total</small>
          <strong>{result.total.total}</strong>
        </div>
        <div>
          <small>Birth / destiny</small>
          <strong>
            {result.profile.birthNumber} / {result.profile.destinyNumber}
          </strong>
        </div>
        <div>
          <small>Lucky totals</small>
          <strong>{result.total.luckyTotals.join(", ") || "-"}</strong>
        </div>
        <div>
          <small>Missing in your DOB</small>
          <strong>{result.profile.missingDobDigits.join(", ") || "none"}</strong>
        </div>
      </div>
      <Findings findings={result.findings} />
      <p className="mn-disclaimer">{result.disclaimer}</p>
    </div>
  );
}

export default function MobileNumberChecker() {
  const { accessToken, isLoggedIn, initializing } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState("mobile");
  const [professions, setProfessions] = useState([]);

  const [mobile, setMobile] = useState("");
  const [dob, setDob] = useState("");
  const [profession, setProfession] = useState("");
  const [pin, setPin] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mobileResult, setMobileResult] = useState(null);
  const [pinResult, setPinResult] = useState(null);

  const [reports, setReports] = useState(null);

  useEffect(() => {
    if (!accessToken) return;
    getNumerologyOptions(accessToken).then((r) => {
      if (r.ok && r.success) setProfessions(r.data.professions || []);
    });
  }, [accessToken]);

  const loadReports = async () => {
    const r = await getMyNumerologyReports(accessToken);
    setReports(r.ok && r.success ? r.data : []);
  };

  useEffect(() => {
    if (tab === "history" && accessToken) loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, accessToken]);

  const submitMobile = async (e) => {
    e.preventDefault();
    setError("");
    const digits = mobile.replace(/\D/g, "");
    if (digits.length < 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }
    const dobText = checkDob(dob);
    if (dobText === null) {
      setError(DOB_ERROR);
      return;
    }
    setLoading(true);
    const r = await analyzeMobileNumber(
      { mobile, ...(dobText ? { dob: dobText } : {}), ...(profession ? { profession } : {}) },
      accessToken,
    );
    setLoading(false);
    if (r.ok && r.success) {
      setMobileResult(r.data);
      setTimeout(() => document.getElementById("mn-result-anchor")?.scrollIntoView({ behavior: "smooth" }), 50);
    } else {
      setMobileResult(null);
      setError(r.message || "Could not analyse this number.");
    }
  };

  const submitPin = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^(\d{4}|\d{6})$/.test(pin)) {
      setError("A PIN must be exactly 4 or 6 digits.");
      return;
    }
    const dobText = checkDob(dob);
    if (!dobText) {
      setError(dobText === "" ? "Please enter your date of birth for the PIN check." : DOB_ERROR);
      return;
    }
    setLoading(true);
    const r = await analyzePin({ pin, dob: dobText }, accessToken);
    setLoading(false);
    if (r.ok && r.success) setPinResult(r.data);
    else {
      setPinResult(null);
      setError(r.message || "Could not analyse this PIN.");
    }
  };

  const openReport = async (id) => {
    const r = await getMyNumerologyReport(id, accessToken);
    if (r.ok && r.success) {
      setMobileResult(r.data.result);
      setMobile(r.data.mobile);
      setDob(formatDob(r.data.dob));
      setTab("mobile");
    }
  };

  const switchTab = (next) => {
    setError("");
    setTab(next);
  };

  // Only signed-in customers can use this tool; everyone else is invited to sign in.
  if (initializing) {
    return (
      <div className="mn-shell">
        <p className="mn-loading">Checking your sign-in...</p>
      </div>
    );
  }
  if (!isLoggedIn) {
    return (
      <div className="mn-shell">
        <div className="mn-signin">
          <h3>Sign in to check a number for free</h3>
          <p>The free number checker is available to registered customers. It takes a minute to sign in or create an account.</p>
          <button
            type="button"
            className="mn-submit"
            onClick={() => navigate("/login", { state: { redirectTo: "/mobile-numerology#checker" } })}
          >
            Sign in to continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mn-shell">
      <div className="mn-tabs" role="tablist">
        {[
          ["mobile", "Mobile number"],
          ["pin", "PIN checker"],
          ["history", "My reports"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`mn-tab ${tab === key ? "is-active" : ""}`}
            onClick={() => switchTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "mobile" && (
        <>
          <form className="mn-form" onSubmit={submitMobile} noValidate>
            <label>
              Mobile number *
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="off"
                maxLength={15}
                placeholder="98765 43210"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
            </label>
            <label>
              Date of birth <small>(recommended)</small>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="bday"
                placeholder="DD/MM/YYYY or DD/MM/YY"
                maxLength={10}
                value={dob}
                onChange={(e) => setDob(maskDob(e.target.value))}
              />
            </label>
            <label>
              Profession <small>(optional)</small>
              <select value={profession} onChange={(e) => setProfession(e.target.value)}>
                <option value="">Any</option>
                {professions.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.title}
                  </option>
                ))}
              </select>
            </label>
            <button className="mn-submit" type="submit" disabled={loading}>
              {loading ? "Analysing..." : "Analyse number"}
            </button>
          </form>
          {error && (
            <div className="mn-error" role="alert">
              {error}
            </div>
          )}
          <div id="mn-result-anchor" />
          {mobileResult && <MobileResult result={mobileResult} />}
        </>
      )}

      {tab === "pin" && (
        <>
          <form className="mn-form" onSubmit={submitPin} noValidate>
            <label>
              Mobile PIN / password digits *
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={6}
                placeholder="4 or 6 digits"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              />
            </label>
            <label>
              Date of birth *
              <input
                type="text"
                inputMode="numeric"
                autoComplete="bday"
                placeholder="DD/MM/YYYY or DD/MM/YY"
                maxLength={10}
                value={dob}
                onChange={(e) => setDob(maskDob(e.target.value))}
              />
            </label>
            <button className="mn-submit" type="submit" disabled={loading}>
              {loading ? "Checking..." : "Check PIN"}
            </button>
          </form>
          <p className="mn-hint">Your PIN is checked on the spot and never saved.</p>
          {error && (
            <div className="mn-error" role="alert">
              {error}
            </div>
          )}
          {pinResult && <PinResult result={pinResult} />}
        </>
      )}

      {tab === "history" && (
        <div className="mn-history">
          {reports === null && <p className="mn-loading">Loading...</p>}
          {reports && reports.length === 0 && <p className="mn-hint">You have not analysed any numbers yet.</p>}
          {reports &&
            reports.map((r) => {
              const v = typeof r.score === "number" ? bandOf(r.score) : VERDICTS[r.verdict] || VERDICTS.ACCEPTABLE;
              return (
                <button key={r.id} type="button" className="mn-history-row" onClick={() => openReport(r.id)}>
                  <strong>{r.mobile}</strong>
                  <span>{r.dob ? `DOB ${formatDob(r.dob)}` : "No DOB"}</span>
                  <span>Total {r.total}</span>
                  <span className={`mn-chip is-${v.cls}`}>{v.label}</span>
                  <small>{new Date(r.createdAt).toLocaleDateString("en-IN", { dateStyle: "medium" })}</small>
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}
